import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo } from '@fortawesome/pro-solid-svg-icons/faInfo';
import { StorableImage } from 'StorableImage';
import { Text } from 'Text';
import { Tooltip } from 'Tooltip';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    Not a card: the title and description sit on the page background.
    The desktop title is text-3xl (32px), the top of the type scale, against the legacy 50px.
*/

type Props = {
    title: string;
    description: string;
    imageSrc: string;
    isBeta: boolean;
    className?: string;
};

export const CourseDescriptionBox: FC<Props> = ({ title, description, imageSrc, isBeta, className }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <div className={twMerge('pb-2.5 sm:pb-3.5', className)}>
            <div className="mb-2.5 flex items-center gap-3.5 sm:gap-5">
                <StorableImage src={imageSrc} alt={title} cacheForever className="h-15 w-15 shrink-0 sm:h-28 sm:w-28" />
                <Text
                    as="h1"
                    color="black"
                    weight="semibold"
                    uppercase
                    className="m-0 text-2xl leading-none sm:text-3xl"
                >
                    {title}
                </Text>
            </div>

            {isBeta && (
                <div className="mt-2.5 flex items-center gap-1">
                    <Text as="div" color="slate-grey" weight="semibold" className="text-xs uppercase">
                        {t('beta')}
                    </Text>
                    <Tooltip content={t('beta_message')}>
                        <span className="max-sm:hidden">
                            <FontAwesomeIcon icon={faInfo} className="text-slate-grey" />
                        </span>
                    </Tooltip>
                </div>
            )}

            <Text
                as="h3"
                weight="semibold"
                uppercase
                className="mb-2.5 ms-5 mt-10 text-base text-slate-grey-500 max-sm:hidden"
            >
                {t('course_description')}
            </Text>
            {/* dir="auto" lets a description in a right-to-left language lay itself out correctly.
                Text does not take dir, so it sits on a wrapper. */}
            <div dir="auto">
                <Text as="p" color="black" className="mx-5 mb-3.5 mt-0 text-sm leading-[1.2] sm:mb-0 sm:text-xl">
                    {description}
                </Text>
            </div>
        </div>
    );
};
